//@ts-check
import '../../styles/B2CSignInEventsForm.css';
import React, { useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/authConfig';
import { useSearchParams } from 'react-router-dom';
import { Form, Label, Input, Button, Alert } from "reactstrap";
import { Spinner } from "react-bootstrap";
import env from 'react-dotenv';
import { FaChevronDown, FaCheck, FaTimes, FaQuestion } from 'react-icons/fa';

const TraceViewer = ({ traces }) => {
    const [search, setSearch] = useState('');
    const [committed, setCommitted] = useState('');
    const [matchIndex, setMatchIndex] = useState(0);
    const preRef = useRef(null);
    const viewerRef = useRef(null);

    const formatted = (() => {
        try {
            return JSON.stringify(JSON.parse(traces), null, 2);
        } catch {
            return traces;
        }
    })();

    const matchCount = committed ? (formatted.toLowerCase().match(new RegExp(committed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').toLowerCase(), 'g')) || []).length : 0;

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (search !== committed) {
                setCommitted(search);
                setMatchIndex(0);
            } else if (matchCount > 0) {
                setMatchIndex(prev => (prev + 1) % matchCount);
            }
        }
    };

    useEffect(() => {
        if (!committed || !preRef.current || !viewerRef.current) return;
        const marks = preRef.current.querySelectorAll('mark');
        if (marks.length > 0 && marks[matchIndex]) {
            marks.forEach(m => m.classList.remove('b2c-signin-traces-active-match'));
            marks[matchIndex].classList.add('b2c-signin-traces-active-match');
            const container = viewerRef.current;
            const mark = marks[matchIndex];
            const markTop = mark.offsetTop - container.offsetTop;
            container.scrollTop = markTop - container.clientHeight / 2;
        }
    }, [committed, matchIndex]);

    const highlightText = (text, query) => {
        if (!query) return text;
        const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === query.toLowerCase() ? <mark key={i}>{part}</mark> : part
        );
    };

    return (
        <div className="b2c-signin-traces-viewer" ref={viewerRef}>
            <div className="b2c-signin-traces-search">
                <input
                    type="text"
                    placeholder="Search traces... (Enter to find)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="b2c-signin-traces-search-input"
                />
                {committed && <span className="b2c-signin-traces-match-count">{matchCount > 0 ? `${matchIndex + 1}/${matchCount}` : '0 results'}</span>}
            </div>
            <pre className="b2c-signin-traces-pre" ref={preRef}>
                {highlightText(formatted, committed)}
            </pre>
        </div>
    );
};

export const B2CSignInEventsForm = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchString, setSearchString] = useState(searchParams.get('q') || "");
    const [daysToSearch, setDaysToSearch] = useState(searchParams.get('days') || "1");
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [eventDetails, setEventDetails] = useState({});
    const [detailLoading, setDetailLoading] = useState(null);
    const [focusEvent, setFocusEvent] = useState(null);
    const [traceData, setTraceData] = useState({});
    const [traceLoading, setTraceLoading] = useState(null);
    const [traceVisible, setTraceVisible] = useState({});
    const timelineRefs = useRef({});
    const initialLoad = useRef(true);

    const { instance, accounts } = useMsal();

    useEffect(() => {
        if (initialLoad.current && searchString) {
            initialLoad.current = false;
            handleSearch({ preventDefault: () => {} });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const getToken = async () => {
        const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
        });
        return tokenResponse.accessToken;
    };

    const handleSearch = async (event) => {
        event.preventDefault();
        if (!searchString.trim()) return;
        setLoading(true);
        setMessage(null);
        setExpandedRow(null);
        try {
            const token = await getToken();
            const encoded = encodeURIComponent(searchString.trim());
            const response = await fetch(`${env.API_BASE_URL}/api/b2c/signinlogs?searchString=${encoded}&daysToSearch=${daysToSearch}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const data = await response.json();
                setResults(data);
                setSearchParams({ q: searchString.trim(), days: daysToSearch });
            } else {
                setResults(null);
                try {
                    const errorData = await response.json();
                    setMessage({ type: 'warning', text: errorData.message || `Status ${response.status}` });
                } catch {
                    const errorText = await response.text();
                    setMessage({ type: 'warning', text: `Status ${response.status}: ${errorText || 'No results found'}` });
                }
            }
        } catch (error) {
            setMessage({ type: 'danger', text: `Error: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = async (correlationId) => {
        if (expandedRow === correlationId) {
            setExpandedRow(null);
            return;
        }
        setExpandedRow(correlationId);

        if (eventDetails[correlationId]) return;

        setDetailLoading(correlationId);
        try {
            const token = await getToken();
            const response = await fetch(`${env.API_BASE_URL}/api/b2c/signinlogs/${correlationId}?daysToSearch=${daysToSearch}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const data = await response.json();
                setEventDetails(prev => ({ ...prev, [correlationId]: data }));
            } else {
                setMessage({ type: 'danger', text: `Failed to load details for ${correlationId}` });
            }
        } catch (error) {
            setMessage({ type: 'danger', text: `Error loading details: ${error.message}` });
        } finally {
            setDetailLoading(null);
        }
    };

    useEffect(() => {
        if (focusEvent && timelineRefs.current[focusEvent]) {
            timelineRefs.current[focusEvent].scrollIntoView({ behavior: 'smooth', block: 'center' });
            timelineRefs.current[focusEvent].classList.add('b2c-signin-event--highlight');
            const el = timelineRefs.current[focusEvent];
            setTimeout(() => el.classList.remove('b2c-signin-event--highlight'), 2000);
            setFocusEvent(null);
        }
    }, [focusEvent, eventDetails]);

    const handleEventBadgeClick = async (correlationId, timestamp) => {
        setFocusEvent(`${correlationId}-${timestamp}`);
        if (expandedRow !== correlationId) {
            await handleRowClick(correlationId);
        }
    };

    const handleViewTraces = async (correlationId) => {
        if (traceVisible[correlationId]) {
            setTraceVisible(prev => ({ ...prev, [correlationId]: false }));
            return;
        }
        setTraceVisible(prev => ({ ...prev, [correlationId]: true }));
        if (traceData[correlationId]) return;

        setTraceLoading(correlationId);
        try {
            const token = await getToken();
            const response = await fetch(`${env.API_BASE_URL}/api/b2c/signinlogs/${correlationId}/traces`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (response.ok) {
                const data = await response.json();
                setTraceData(prev => ({ ...prev, [correlationId]: data }));
            } else {
                setMessage({ type: 'danger', text: `Failed to load traces for ${correlationId}` });
                setTraceVisible(prev => ({ ...prev, [correlationId]: false }));
            }
        } catch (error) {
            setMessage({ type: 'danger', text: `Error loading traces: ${error.message}` });
            setTraceVisible(prev => ({ ...prev, [correlationId]: false }));
        } finally {
            setTraceLoading(null);
        }
    };

    const formatTimestamp = (ts) => {
        if (!ts) return '--';
        const d = new Date(ts);
        return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    return (
        <div className="b2c-signin-container">
            <h3>B2C Sign-In Events</h3>

            <Form onSubmit={handleSearch} className="b2c-signin-search-bar">
                <div className="d-flex align-items-center gap-2">
                    <Input
                        type="text"
                        id="searchString"
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        disabled={loading}
                        className="flex-grow-1"
                        placeholder="Search sign-in events..."
                    />
                    <Input
                        type="select"
                        value={daysToSearch}
                        onChange={(e) => setDaysToSearch(e.target.value)}
                        disabled={loading}
                        style={{ width: 'auto', minWidth: '130px' }}
                    >
                        <option value="1">24 hours</option>
                        <option value="7">7 days</option>
                        <option value="14">14 days</option>
                        <option value="30">30 days</option>
                    </Input>
                    <Button disabled={loading || !searchString.trim()} type="submit" className="button" style={{ whiteSpace: 'nowrap' }}>
                        {loading ? <Spinner animation="border" size="sm" /> : 'Search'}
                    </Button>
                </div>
            </Form>

            {message && (
                <div className="mt-3">
                    <Alert color={message.type}>{message.text}</Alert>
                </div>
            )}

            {results && (
                <>
                    <div className="b2c-signin-meta">
                        {results.returnedResults} result{results.returnedResults !== 1 ? 's' : ''} for "<strong>{results.searchString}</strong>" (last {results.daysSearched} days)
                        {results.approximateTotal > results.returnedResults && (
                            <span> &middot; ~{results.approximateTotal.toLocaleString()} total matches</span>
                        )}
                    </div>

                    <div className="b2c-signin-results">
                        {[...results.results].sort((a, b) => new Date(b.LastSeen) - new Date(a.LastSeen)).map((item) => {
                            const exceptions = item.Exceptions || [];
                            const hasError = exceptions.length > 0;
                            const isComplete = !!item.SignInComplete;
                            const status = hasError ? 'error' : isComplete ? 'success' : 'abandoned';
                            const statusLabel = hasError ? 'Error' : isComplete ? 'Complete' : 'Abandoned';
                            const isExpanded = expandedRow === item.CorrelationID;
                            const detail = eventDetails[item.CorrelationID];
                            const eventNames = item.EventNames || [];

                            return (
                                <div key={item.CorrelationID} className={`b2c-signin-card b2c-signin-card--${status}`}>
                                    <div className="b2c-signin-card-header" onClick={() => handleRowClick(item.CorrelationID)}>
                                        <div className={`b2c-signin-status ${status}`}>
                                            {hasError ? <FaTimes /> : isComplete ? <FaCheck /> : <FaQuestion />}
                                        </div>
                                        <div className="b2c-signin-card-title">
                                            <span className="b2c-signin-card-user">
                                                {item.ManagedUsername || item.signInName || 'Unknown User'}
                                            </span>
                                            <span className={`b2c-signin-status-label b2c-signin-status-label--${status}`}>{statusLabel}</span>
                                        </div>
                                        <div className="b2c-signin-card-times">
                                            <span>{formatTimestamp(item.SignInStarted)}</span>
                                            <span>{item.LastSeen ? `\u2192 ${formatTimestamp(item.LastSeen)}` : ''}</span>
                                        </div>
                                        <FaChevronDown className={`b2c-signin-expand-icon ${isExpanded ? 'expanded' : ''}`} />
                                    </div>

                                    <div className="b2c-signin-card-body">
                                        <div className="b2c-signin-card-layout">
                                            <table className="b2c-signin-props-table">
                                                <tbody>
                                                    <tr><th>Policy</th><td>{item.PolicyId || '--'}</td></tr>
                                                    <tr><th>App ID</th><td>{item.AppId || '--'}</td></tr>
                                                    <tr><th>Domain Hint</th><td>{item.DomainHint || '--'}</td></tr>
                                                    <tr><th>Allow JIT</th><td>{item.AllowJit || '--'}</td></tr>
                                                    <tr><th>Existing Session</th><td>{item.ExistingSession || '--'}</td></tr>
                                                    <tr><th>Sign-In Name</th><td>{item.signInName || '--'}</td></tr>
                                                    <tr><th>Managed Username</th><td>{item.ManagedUsername || '--'}</td></tr>
                                                    <tr><th>Correlation ID</th><td>{item.CorrelationID}</td></tr>
                                                </tbody>
                                            </table>
                                            <div className="b2c-signin-events-sidebar">
                                                <label>Events</label>
                                                <div className="b2c-signin-event-badges">
                                                    {(() => {
                                                        const allEvents = [
                                                            ...eventNames.map(evt => ({ ...evt, isError: false })),
                                                            ...exceptions.map(exc => ({ name: 'Exception', timestamp: exc.timestamp, isError: true }))
                                                        ];
                                                        allEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                                                        return allEvents.map((evt, i) => {
                                                            const badgeClass = evt.isError ? 'b2c-signin-event-badge--error' : evt.name === 'SignInComplete' ? 'b2c-signin-event-badge--success' : '';
                                                            return (
                                                                <div
                                                                    key={i}
                                                                    className={`b2c-signin-event-badge b2c-signin-event-badge--clickable ${badgeClass}`}
                                                                    onClick={(e) => { e.stopPropagation(); handleEventBadgeClick(item.CorrelationID, evt.timestamp); }}
                                                                >
                                                                    <span className="b2c-signin-event-badge-name">{evt.name}</span>
                                                                    <span className="b2c-signin-event-badge-time">{formatTimestamp(evt.timestamp)}</span>
                                                                </div>
                                                            );
                                                        });
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                        {hasError && (
                                            <div className="b2c-signin-error-block">
                                                <label>Exception{exceptions.length > 1 ? 's' : ''}</label>
                                                {exceptions.map((exc, i) => (
                                                    <div key={i} className="b2c-signin-error-entry">
                                                        <span className="b2c-signin-error-time">{formatTimestamp(exc.timestamp)}</span>
                                                        <span>{exc.message}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {isExpanded && (
                                        <div className="b2c-signin-drilldown">
                                            {detailLoading === item.CorrelationID && (
                                                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                                            )}
                                            {detail && (
                                                <div className="b2c-signin-timeline">
                                                    {detail.events.map((evt, idx) => (
                                                        <div
                                                            key={idx}
                                                            ref={el => { timelineRefs.current[`${item.CorrelationID}-${evt.timestamp}`] = el; }}
                                                            className={`b2c-signin-event ${evt.type === 'exception' ? 'b2c-signin-event--exception' : ''}`}
                                                        >
                                                            <div className="b2c-signin-event-header">{evt.name}</div>
                                                            <div className="b2c-signin-event-time">{formatTimestamp(evt.timestamp)}</div>
                                                            {evt.type === 'exception' ? (
                                                                <div className="b2c-signin-event-exception">
                                                                    <p>{evt.details.message}</p>
                                                                    {evt.details.exceptionType && (
                                                                        <span className="b2c-signin-event-exception-type">{evt.details.exceptionType}</span>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <dl className="b2c-signin-dims">
                                                                    {Object.entries(evt.details || {}).map(([key, value]) => (
                                                                        <React.Fragment key={key}>
                                                                            <dt>{key}</dt>
                                                                            <dd>{value || '--'}</dd>
                                                                        </React.Fragment>
                                                                    ))}
                                                                </dl>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="b2c-signin-traces-section">
                                                <button
                                                    className="b2c-signin-traces-btn"
                                                    onClick={(e) => { e.stopPropagation(); handleViewTraces(item.CorrelationID); }}
                                                    disabled={traceLoading === item.CorrelationID}
                                                >
                                                    {traceLoading === item.CorrelationID ? <Spinner animation="border" size="sm" /> : traceVisible[item.CorrelationID] ? 'Hide Traces' : 'View Traces'}
                                                </button>
                                                {traceVisible[item.CorrelationID] && traceData[item.CorrelationID] && (
                                                    <TraceViewer traces={traceData[item.CorrelationID].traces} />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};
